/**
 * Vanilla JS SPA Router Implementation
 * History APIを使用したルーティングシステム
 */
import { initializeHome } from "../home/home";
import { initializeDataList } from "../pages/dataList";
import { initializeReplacement } from "../pages/replacement";
import { initializeIndividualEquipment } from "../pages/individualEquipment";
import { initializeMockPage } from "../pages/mockPage";
import { initializeFieldSurveyRequestList } from "../pages/fieldSurveyRequestList";
import { initializeFieldSurveyRequestNew } from "../pages/fieldSurveyRequestNew";
import { initializeFieldSurveyRequestEdit } from "../pages/fieldSurveyRequestEdit";
import { initializeFieldSurveyRequestApprove } from "../pages/fieldSurveyRequestApprove";
import { initializeFieldSurveyRequestHistory } from "../pages/fieldSurveyRequestHistory";
import { initializeFieldSurveyRequestRetry } from "../pages/fieldSurveyRequestRetry";
import { isFirebaseAuth } from "../apis/firebaseAuthentication";
import { PAGE_LOGIN } from "../constant/systemConstant";
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from "../apis/firebase";
// 認証状態管理
let isAuthChecked = false;
let pendingRoute = null;
// ルート定義
const routes = [
    {
        path: '/',
        view: initializeHome,
        requiresAuth: true
    },
    {
        path: '/store-list',
        view: initializeDataList,
        requiresAuth: true
    },
    {
        path: '/replacement/:projectId',
        view: (params) => {
            if (params?.projectId) {
                initializeReplacement(params.projectId);
            }
        },
        requiresAuth: true
    },
    {
        path: '/field-survey-request-list',
        view: initializeFieldSurveyRequestList,
        requiresAuth: true
    },
    {
        path: '/field-survey-request-new',
        view: initializeFieldSurveyRequestNew,
        requiresAuth: true
    },
    {
        path: '/field-survey-request-edit/:requestId',
        view: (params) => {
            initializeFieldSurveyRequestEdit(params?.requestId);
        },
        requiresAuth: true
    },
    {
        path: '/field-survey-request-approve/:requestId',
        view: (params) => {
            initializeFieldSurveyRequestApprove(params?.requestId);
        },
        requiresAuth: true
    },
    {
        path: '/field-survey-request-history/:requestId',
        view: (params) => {
            initializeFieldSurveyRequestHistory(params?.requestId);
        },
        requiresAuth: true
    },
    {
        path: '/field-survey-request-retry/:requestId',
        view: (params) => {
            initializeFieldSurveyRequestRetry(params?.requestId);
        },
        requiresAuth: true
    },
    {
        path: '/individual-equipment/:projectId',
        view: (params) => {
            if (params?.projectId) {
                initializeIndividualEquipment(params.projectId);
            }
        },
        requiresAuth: true
    },
    {
        path: '/layout-view',
        view: () => initializeMockPage('/layoutView.html', 'レイアウト閲覧'),
        requiresAuth: true
    },
    {
        path: '/layout-bulk-download',
        view: () => initializeMockPage('/layoutBulkDownload.html', 'レイアウト一括取得アプリ'),
        requiresAuth: true
    },
    {
        path: '/store-change-batch',
        view: () => initializeMockPage('/storeChangeBatch.html', '店番変更バッチ実行画面'),
        requiresAuth: true
    }
];
/**
 * パスとルートをマッチングし、パラメータを抽出する
 * @param pathname 現在のパス
 * @returns マッチしたルートとパラメータ、またはnull
 */
function matchRoute(pathname) {
    for (const route of routes) {
        const paramNames = [];
        // :paramName を正規表現に変換
        const regexPath = route.path.replace(/:([^/]+)/g, (_, key) => {
            paramNames.push(key);
            return '([^/]+)';
        });
        const regex = new RegExp(`^${regexPath}$`);
        const match = pathname.match(regex);
        if (match) {
            const params = {};
            paramNames.forEach((key, i) => {
                params[key] = match[i + 1];
            });
            return { view: route.view, params };
        }
    }
    return null;
}
/**
 * 認証状態を監視してルーティングを実行
 */
function setupAuthStateListener() {
    onAuthStateChanged(auth, (_user) => {
        isAuthChecked = true;
        if (pendingRoute) {
            const routeToProcess = pendingRoute;
            pendingRoute = null;
            window.history.replaceState(null, '', routeToProcess);
            router();
        }
    });
}
/**
 * 現在のパスに基づいてルーティングを実行
 */
export function router() {
    const pathname = window.location.pathname;
    const match = matchRoute(pathname);
    if (match) {
        // ルートが認証を必要とする場合、認証状態をチェック
        const route = routes.find(r => {
            const paramNames = [];
            const regexPath = r.path.replace(/:([^/]+)/g, (_, key) => {
                paramNames.push(key);
                return '([^/]+)';
            });
            const regex = new RegExp(`^${regexPath}$`);
            return pathname.match(regex);
        });
        if (route && route.requiresAuth) {
            // 認証状態がまだ確定していない場合は待機
            if (!isAuthChecked) {
                pendingRoute = pathname;
                return;
            }
            // 認証されていない場合はログインページにリダイレクト
            if (!isFirebaseAuth()) {
                window.location.href = '/' + PAGE_LOGIN;
                return;
            }
        }
        try {
            window.scrollTo(0, 0);
            // マッチしたルートのビューを実行
            match.view(match.params);
        }
        catch (error) {
            console.error('Router: ビュー実行エラー', error);
            show404();
        }
    }
    else {
        show404();
    }
}
/**
 * 404ページを表示
 */
function show404() {
    const subContents = document.getElementById('subContents');
    if (subContents) {
        subContents.innerHTML = `
      <div style="text-align: center; padding: 50px;">
        <h1>404 - ページが見つかりません</h1>
        <p>お探しのページは存在しません。</p>
        <a href="/" data-link>ホームに戻る</a>
      </div>
    `;
        // 404ページ内のリンクにもイベントリスナーを設定
        const homeLink = subContents.querySelector('a[data-link]');
        if (homeLink) {
            homeLink.addEventListener('click', (e) => {
                e.preventDefault();
                navigate('/');
            });
        }
    }
}
/**
 * プログラムによるナビゲーション
 * @param path 遷移先のパス
 */
export function navigate(path) {
    window.history.pushState(null, '', path);
    window.scrollTo(0, 0);
    router();
}
/**
 * リンククリックのハンドリング
 * data-link属性を持つリンクをSPAナビゲーションとして処理
 */
function handleLinkClick(event) {
    const target = event.target;
    if (target.matches('[data-link]')) {
        event.preventDefault();
        const href = target.getAttribute('href');
        if (href) {
            navigate(href);
        }
    }
}
/**
 * ルーターの初期化
 * イベントリスナーを設定し、初回ルーティングを実行
 */
export function initRouter() {
    // 認証状態監視を設定
    setupAuthStateListener();
    // ブラウザの戻る/進むボタンのハンドリング
    window.addEventListener('popstate', router);
    // data-link属性を持つリンクのクリックハンドリング
    document.addEventListener('click', handleLinkClick);
    // 初回ルーティング実行
    router();
}
/**
 * ルーターの破棄
 * イベントリスナーを削除
 */
export function destroyRouter() {
    window.removeEventListener('popstate', router);
    document.removeEventListener('click', handleLinkClick);
}
